import type { ReactNode } from 'react';
import { useWorkspaceRoot } from '@/lib/api';
import {
  ApiError,
  isNotFoundError,
  isRateLimitedError,
  isServerError,
} from '@/lib/api/core';
import { requestErrorCopy } from '@/lib/api/error-copy';
import { useAuthState } from '@/lib/auth-state';
import {
  shouldRedirectAnonymousToSignIn,
  useAnonymousSignInRedirect,
} from '@/lib/sign-in-redirect';

interface OwnerWorkspaceGateProps {
  owner: string;
  children: (props: {
    organizationId: string | null;
    ownerSlug: string;
    isWorkspaceLoading: boolean;
  }) => ReactNode;
}

/**
 * Resolves the org workspace for owner-scoped pages. Always renders children;
 * while workspace is loading passes `organizationId: null` so consumers paint
 * page chrome immediately and swap in shape-matched body skeletons.
 */
export function OwnerWorkspaceGate({ owner, children }: OwnerWorkspaceGateProps) {
  const { data, isLoading, error, refetch } = useWorkspaceRoot(owner, { slug: owner });
  const { session, isReady: authReady } = useAuthState();

  // Three outcomes, not two. A 404 is "no such organization". A 5xx or a
  // transport failure is an outage. Everything between — chiefly the 401 that
  // `resolve_path` answers for anonymous access to a private org — is neither,
  // and falls through to the sign-in redirect below. Calling those an outage
  // suppressed the redirect and stranded visitors on an error page.
  //
  // A failed REFETCH that still has a workspace cached is not an error state
  // at all: keep showing the page that already works.
  const isNotFound = isNotFoundError(error);
  const isUnavailable = isServerError(error) && !isNotFound && !data?.workspace;
  // A 429 is neither an outage nor a missing session: signing in may help
  // eventually, but the page that got us here was readable, so bouncing a
  // visitor to the login route hides the one thing they need to know — come
  // back in N seconds.
  const isRateLimited = isRateLimitedError(error) && !data?.workspace;

  // Outages and rate limits render their own state; only a refusal signs in.
  useAnonymousSignInRedirect({
    active: shouldRedirectAnonymousToSignIn({
      settled: !isLoading,
      authReady,
      session,
      resolved: !!data?.workspace,
      error,
    }),
    owner,
  });

  const workspace = data?.workspace;

  if (isLoading) {
    return (
      <>
        {children({
          organizationId: null,
          ownerSlug: owner,
          isWorkspaceLoading: true,
        })}
      </>
    );
  }

  if (isUnavailable) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center justify-center gap-1 py-12 text-center"
      >
        <p className="text-muted-foreground">This workspace could not be loaded.</p>
        <p className="text-xs text-muted-foreground/70">Please try again in a moment.</p>
      </div>
    );
  }

  if (isRateLimited) {
    const copy = requestErrorCopy(error, { subject: "This workspace" });
    return (
      <div
        role="alert"
        data-error-code={error instanceof ApiError ? error.code : undefined}
        className="flex flex-col items-center justify-center gap-2 py-12 text-center"
      >
        <p className="font-medium">{copy.title}</p>
        <p className="text-muted-foreground max-w-md text-sm">{copy.body}</p>
        {copy.retryable ? (
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-sm font-medium underline underline-offset-4"
          >
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  if (isNotFound || !workspace) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        Organization not found
      </div>
    );
  }

  if (workspace.type !== 'organization') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 py-16 text-center">
        <p className="text-muted-foreground max-w-md">
          Select an organization to view this page.
        </p>
      </div>
    );
  }

  return (
    <>
      {children({
        organizationId: workspace.id,
        ownerSlug: workspace.slug,
        isWorkspaceLoading: false,
      })}
    </>
  );
}
